import { addBusinessDays, subBusinessDays, isWeekend, differenceInBusinessDays, nextMonday, startOfDay, addDays, subDays } from 'date-fns';

/**
 * Calculate new start and end dates after a drag operation.
 * Shifts both dates by the given number of business days.
 * Negative daysDelta moves the task earlier; positive moves it later.
 * Weekends are skipped — the task duration in business days is preserved.
 */
export const calculateDragDates = (
    startDate: Date,
    endDate: Date,
    daysDelta: number
): { newStartDate: Date; newEndDate: Date } => ({
    newStartDate: addBusinessDays(startDate, daysDelta),
    newEndDate: addBusinessDays(endDate, daysDelta),
});

/**
 * Convert a horizontal pixel delta from a drag operation to a business-day offset.
 * @param deltaX - pixel distance moved (positive = right/forward, negative = left/backward)
 * @param pixelsPerDay - current timeline scale (pixels per calendar day)
 */
// `|| 0` normalises -0 (Math.round(-0.333)) to plain 0, which satisfies Object.is comparisons.
export const pixelsToDaysDelta = (deltaX: number, pixelsPerDay: number): number =>
    Math.round(deltaX / pixelsPerDay) || 0;

/**
 * Date utilities for task date calculations with weekend exclusion
 */

/**
 * Calculate end date from start date and duration (in business days)
 * Automatically skips weekends
 */
export const calculateEndDate = (
    startDate: Date,
    durationDays: number
): Date => {
    return addBusinessDays(startDate, durationDays - 1); // -1 because start date counts as day 1
};

/**
 * Check if a date falls on a weekend
 */
export const isDateWeekend = (date: Date): boolean => {
    return isWeekend(date);
};

/**
 * Adjust date to next business day if it falls on a weekend
 */
export const adjustToBusinessDay = (date: Date): Date => {
    if (isWeekend(date)) {
        // If Saturday, move to Monday
        return nextMonday(startOfDay(date));
    }
    return startOfDay(date);
};

/**
 * Calculate business days between two dates (excluding weekends)
 */
export const calculateBusinessDaysBetween = (startDate: Date, endDate: Date): number => {
    return differenceInBusinessDays(endDate, startDate) + 1; // +1 to include both start and end dates
};

/**
 * Returns the next working day after the given date.
 * Always advances by at least 1 calendar day, then skips weekends.
 */
export const getNextWorkingDay = (date: Date): Date => {
    let next = addDays(date, 1);
    while (isWeekend(next)) {
        next = addDays(next, 1);
    }
    return next;
};

/**
 * Returns the previous working day before the given date.
 * Always goes back at least 1 calendar day, then skips weekends.
 */
export const getPreviousWorkingDay = (date: Date): Date => {
    let prev = subDays(date, 1);
    while (isWeekend(prev)) {
        prev = subDays(prev, 1);
    }
    return prev;
};

/**
 * Subtracts the given number of business days from a date, excluding weekends.
 */
export const subtractWorkingDays = (date: Date, days: number): Date => {
    return subBusinessDays(date, days);
};

/**
 * Compute new start and end dates when a task is moved to a new start date.
 * Preserves the task's working-day duration.
 */
export const updateTaskDates = (
    startDate: Date,
    endDate: Date,
    newStartDate: Date
): { newStartDate: Date; newEndDate: Date } => {
    const duration = calculateBusinessDaysBetween(startDate, endDate);
    const newEndDate = calculateEndDate(newStartDate, duration);
    return { newStartDate, newEndDate };
};

/**
 * Validate that a task's dates are internally consistent.
 * Logs a warning and returns false for any violation.
 */
export const validateTaskDateIntegrity = (startDate: Date, endDate: Date): boolean => {
    if (endDate < startDate) {
        console.warn('Task integrity violation: end date is before start date', { startDate, endDate });
        return false;
    }
    const duration = calculateBusinessDaysBetween(startDate, endDate);
    if (duration < 0) {
        console.warn('Task integrity violation: negative duration calculated', { startDate, endDate, duration });
        return false;
    }
    return true;
};

/**
 * Format date for input field (YYYY-MM-DD)
 */
export const formatDateForInput = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

